import { compactByProps, concat, notNullOrEmpty } from 'shared/collection';
import { Bounds, isLogisticValid, ForecastDrawn, ForecastLogistic, Logistic, zForecast, Fmt, Forecast as F, ForecastQuestionCfgAmount, QRComparison } from 'shared/forecast';
import { notNull } from 'shared/type';
import { doSafe } from 'shared/promise';
import { DeltaValues } from 'shared/paths';
import { ChildrenProps } from 'shared/ui/uiType';
import { Rnge } from 'shared/range';
import { max } from 'shared/agg';
import { useMemoValue, LabelForm, Markdown, WrapIf } from 'shared/ui';
import { Dist, DistributionLabel, DistLogistic } from 'shared/chart';
import { ResultColorFn } from 'shared/forecast/results/QuestionResults';
import { match, P } from 'ts-pattern';
import { ForecastDrawnEdit, makePoint } from './ForecastDrawnEdit';
import { ForecastLogisticEdit } from './ForecastLogisticEdit';
import { ForecastComponentResolution } from './Forecast';
import { useSafeMantineTheme, Combobox as Combo, CheckIcon, InputBase, useCombobox, Alert, Switch, Tooltip, ActionIcon, Modal } from '@mantine/core';
import { ReactNode, useMemo } from 'react';
import { MdClose, MdFullscreen, MdWarning } from 'react-icons/md';
import { QRAmount } from 'shared/forecast/results';
import { keys, pick, pipe } from 'remeda';
import { HiOutlineArrowCircleUp, HiOutlineMinusCircle, HiOutlinePlusCircle, HiOutlineSearchCircle } from 'react-icons/hi';
import { IconContext } from 'react-icons';
import { useDisclosure } from '@mantine/hooks';
import { twMerge } from 'tailwind-merge';
import { ActionCard } from 'components/ActionPanel';
export type AmountForecastFetched = {
  id: string;
  cfg: ForecastDrawn | ForecastLogistic;
};
type ForecastCfgDelta = DeltaValues<ForecastLogistic | ForecastDrawn>;
export type ForecastAmountProps = {
  forecast: ForecastDrawn | ForecastLogistic;
  previousForecast?: AmountForecastFetched;
  resolution: ForecastComponentResolution;
  question: Pick<ForecastQuestionCfgAmount, 'defaultBounds' | 'forecastType' | 'unitLabel' | 'unitLabelLong' | 'numberFormat' | 'title' | 'amountType' | 'aggVersion' | 'chart' | 'colors' | 'hardBounds' | 'comparisons'>;
  onDelta: (value: ForecastCfgDelta) => void;
  showGroup?: boolean;
  color: ResultColorFn;
  menuItems?: ReactNode;
  // /** separate comparisons because the user can manage their own that is not inside the question cfg */
  // comparisons?: QRComparison[]
} & ChildrenProps;
const forecastTypeMd = {
  logistic: {
    label: 'Logistic',
    desc: 'A simple probability density curve. You set the 50th percentile and a left/right skew and we generate a sensible logistic distribution.'
  },
  drawn: {
    label: 'Drawn',
    desc: `Control your forecast by drawing your probability curve over a range of values.

*NOTE:* switching to this will default to an approximate of the current logistic curve.`
  }
};
const forecastTypes = keys(forecastTypeMd);
export const ForecastAmount = ({
  forecast,
  previousForecast,
  resolution,
  question,
  showGroup,
  color,
  onDelta,
  menuItems,
  children
}: ForecastAmountProps) => {
  const isConfigured = F.amountConfigured(forecast);
  const forecasts = useMemo(() => (resolution.forecasts?.filter(f => forecastTypes.includes(f.cfg.type as any) && F.shouldShow(f)) ?? []).map(f_0 => ({
    ...f_0,
    value: f_0.value ?? F.getValue({
      f: f_0.cfg,
      q: question
    })
  })), [resolution.forecasts]);
  const dists = useMemo(() => isConfigured ? compactByProps(forecasts.map(f_1 => ({
    dist: F.amountDist({
      f: f_1.cfg,
      q: question
    }),
    forecast: f_1
  })), ['dist']) : undefined, [forecasts, isConfigured, question]);
  const {
    bounds
  } = useMemoValue(Bounds.withDefaults(forecast.bounds, question.defaultBounds));
  const previousDist = useMemo(() => previousForecast ? F.amountDist({
    f: previousForecast.cfg,
    q: question
  }) : undefined, [previousForecast, question]);
  const currentDist = useMemo(() => F.amountDist({
    f: forecast,
    q: question
  }), [forecast, question]);
  const groupDist = useMemo(() => showGroup && notNullOrEmpty(dists) ? Dist.agg(dists.map(d => d.dist), {
    displayBounds: Bounds.toTuple(question.defaultBounds),
    // keep at default bounds
    hardBounds: Bounds.toTuple(question.hardBounds)
  }) : undefined, [showGroup, bounds, dists]);
  const previous50th = previousDist?.percentile50;
  const {
    colors
  } = useSafeMantineTheme();
  const {
    unitLabel,
    numberFormat,
    amountType,
    defaultBounds
  } = question;
  const fmtStrings = useMemoValue(Fmt.getOption({
    unitLabel,
    numberFormat,
    forecastType: 'amount',
    mode: 'absolute',
    amountType
  }));
  const {
    questionAgg
  } = resolution;
  const dist = {
    ...currentDist,
    labels: currentDist ? concat<DistributionLabel>(showGroup && previous50th != currentDist.percentile50 ? {
      x: previous50th,
      title: 'Saved 50th',
      color: colors.gray[4],
      type: 'xBottom'
    } as DistributionLabel : undefined, Logistic.percentileLabels(currentDist, {
      showTitle: true
    }), Logistic.label50th(currentDist, {
      format: fmtStrings.default,
      color: color('default'),
      type: 'mainPoint',
      showLabelAtTop: true,
      subTitle: '50th percentile'
    }), forecast.type == 'drawn' ? Logistic.labelBounds({
      bounds: defaultBounds,
      color: color('question')
    }) : undefined, doSafe(questionAgg?.you, x => Logistic.labelComparison({
      x,
      color: color('question'),
      title: 'baseline'
    }))) : []
  };
  const screenGroupDist = doSafe(groupDist, d_0 => ({
    ...d_0,
    labels: concat(Logistic.percentileLabels(d_0), Logistic.label50th(d_0, {
      format: fmtStrings.default
    }), doSafe(questionAgg?.group, x_0 => Logistic.labelComparison({
      x: x_0,
      color: color('question'),
      title: 'baseline'
    })))
  }));
  const [fullWidth, fullWidthCtrl] = useDisclosure(false);
  const margin = forecast.type == 'drawn' ? {
    left: 30,
    right: 30
  } : {
    left: 16,
    right: 16
  };
  const editCommon = {
    question,
    questionAgg,
    showGroup,
    color,
    margin,
    groupDist,
    dist
    // style: fullWidth ? {height: '24rem'} : {height: '18rem'}
  };
  // const fmtOptions = Fmt.getOptions(question)
  const forecastTypeCombo = useCombobox();
  const zoom = (factor: number) => {
    const {
      min,
      max
    } = bounds;
    const w = (max - min) * factor;
    onDelta({
      bounds: match({
        isMinBound: min === question.defaultBounds.min,
        isMaxBound: max === question.defaultBounds.max,
        p50: currentDist?.percentile50
      }).with({
        isMinBound: true
      }, () => ({
        min,
        max: min + w
      })).with({
        isMaxBound: true
      }, () => ({
        min: max - w,
        max
      })).with({
        p50: P.not(P.nullish)
      }, ({
        p50
      }) => ({
        min: p50 - w / 2,
        max: p50 + w / 2
      })).otherwise(() => ({
        min: min + w / 2,
        max: max - w / 2
      }))
    });
  };
  return <WrapIf if={fullWidth} wrap={c => <Modal zIndex={20} opened={fullWidth} size='100%' centered withCloseButton={false} onClose={fullWidthCtrl.close}>
          {c}
        </Modal>}>
      <div className='bg-white flex flex-col gap-1'>
        <ActionCard variant='inline' classNames={{
        root: 'ml-auto'
      }}>
          {menuItems}
          {(forecast.type == 'logistic' || showGroup && groupDist != null) && <Tooltip label={<div className='space-y-2'>
                  <div>Toggle between cumulative probability and a probability density views</div>
                  <div className='opacity-60'>
                    Note: quantity distribution editing is currently limited to probability density
                  </div>
                </div>}>
              <div>
                <Switch onLabel='Cumulative' offLabel='Probability' size='lg' styles={{
              trackLabel: {
                padding: '0 1.2em',
                fontSize: '0.8rem'
              }
            }} checked={forecast.cumulative} onChange={v => onDelta({
              cumulative: v.target.checked
            })} />
              </div>
            </Tooltip>}
          <Combo store={forecastTypeCombo} onOptionSubmit={v_0 => {
          forecastTypeCombo.closeDropdown();
          if (!v_0) return;
          const newForecast = zForecast.parse({
            ...forecast,
            type: v_0
          });
          if (forecast?.type == 'logistic' && isLogisticValid(forecast) && newForecast.type == 'drawn') {
            // convert points from logistic to drawn
            const dist_0 = Dist.pdfToDiscrete(DistLogistic.logisticToPdf(Logistic.toDistLogisticCfg({
              f: forecast,
              q: question
            })));
            const pts = Rnge.create([dist_0.percentileX(0.001) ?? dist_0.bounds[0], dist_0.percentileX(0.999) ?? dist_0.bounds[1]], 11).map(x_1 => makePoint(dist_0.adjustedPt(x_1)));
            const maxP = max(pts.map(p => p.p));
            newForecast.points = pts.map((p_0, i) => ({
              ...p_0,
              p: i == 0 || i == pts.length - 1 ? 0 : p_0.p / maxP * 0.8
            }));
          }
          onDelta({
            '.': newForecast
          } as any);
        }}>
            <Combo.Dropdown className='!p-1 !max-w-[30em]'>
              <Combo.Header>
                <LabelForm className='pb-1 pl-2 pt-2'>Edit mode</LabelForm>
              </Combo.Header>
              <Combo.Options className='space-y-1'>
                {forecastTypes.map(value => {
                const {
                  label,
                  desc
                } = forecastTypeMd[value];
                return <Combo.Option key={value} value={value} active={forecast.type === value} className=''>
                      <div className='flex items-center gap-2'>
                        {forecast.type === value && <CheckIcon size={12} />}
                        <div className='font-semibold'>{label}</div>
                      </div>
                      <Markdown>{desc}</Markdown>
                      {value == 'logistic' && forecast.type == 'drawn' && <Alert color='amber' icon={<MdWarning />} title='Edits to drawing will be lost'>
                          By switching to a logistic curve, you will loose the current drawing.
                        </Alert>}
                    </Combo.Option>;
              })}
              </Combo.Options>
            </Combo.Dropdown>
            <Combo.Target>
              <InputBase type='button' component='button' variant='unstyled' pointer size='sm' rightSectionPointerEvents='none' onClick={() => forecastTypeCombo.toggleDropdown()} styles={{
              input: {
                display: 'flex'
              }
            }} classNames={{
              input: 'items-center gap-2'
            }}>
                <div className='flex items-center'>
                  <div className='font-semibold'>{forecastTypeMd[forecast.type].label}</div>
                  <Combo.Chevron />
                </div>
              </InputBase>
            </Combo.Target>
          </Combo>
          <div className='flex gap-1'>
            <IconContext.Provider value={{
            size: '1.2rem'
          }}>
              <Tooltip label='Zoom in'>
                <ActionIcon onClick={() => zoom(0.5)}>
                  <HiOutlinePlusCircle />
                </ActionIcon>
              </Tooltip>
              <Tooltip label='Zoom out'>
                <ActionIcon onClick={() => zoom(2)}>
                  <HiOutlineMinusCircle />
                </ActionIcon>
              </Tooltip>
              <Tooltip label='Zoom to fit'>
                <ActionIcon onClick={() => {
                const w_0 = Rnge.width(Bounds.toTuple(forecast.bounds));
                const distBoundless = F.amountDist({
                  f: {
                    ...forecast,
                    tails: {},
                    hardBounds: undefined
                  },
                  q: {
                    ...question,
                    hardBounds: {}
                  }
                });
                const getBound = (mode: 'min' | 'max') => {
                  const distPercentile = dist?.percentileX?.(mode === 'min' ? 0 : 0.999);
                  const boundlessPercentile = doSafe(distBoundless?.percentileX(mode === 'min' ? 0.1 : 0.9), x_2 => x_2 + (mode === 'min' ? -w_0 * 0.01 : w_0 * 0.01));
                  const groupPercentile = showGroup ? screenGroupDist?.percentileX(mode === 'min' ? 0 : 1) : undefined;
                  const hardBound = question?.hardBounds?.[mode];
                  const res = pipe([distPercentile, boundlessPercentile, groupPercentile, hardBound].filter(notNull), xs => xs.length > 0 ? mode === 'min' ? Math.min(...xs) : Math.max(...xs) : question.defaultBounds[mode]);
                  return res;
                };
                const min_0 = getBound('min');
                const max_0 = getBound('max');
                onDelta({
                  bounds: {
                    min: min_0,
                    max: max_0
                  }
                });
              }}>
                  <HiOutlineSearchCircle />
                </ActionIcon>
              </Tooltip>
              <Tooltip label='Reset zoom'>
                <ActionIcon onClick={() => {
                onDelta({
                  bounds: question.defaultBounds
                });
              }}>
                  <HiOutlineArrowCircleUp />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={fullWidth ? 'Exit large editor' : 'Show in a full-width editor'}>
                <ActionIcon onClick={() => fullWidthCtrl.toggle()} color='gray'>
                  {fullWidth ? <MdClose /> : <MdFullscreen />}
                </ActionIcon>
              </Tooltip>
            </IconContext.Provider>
          </div>
        </ActionCard>
        {children}
        {doSafe(showGroup && screenGroupDist ? screenGroupDist : undefined, d_1 => <div>
            <QRAmount {...pick(resolution, ['code', 'resolution', 'group', 'baselineAgg', 'questionAgg', 'resAgg', 'comparisons'])} forecasts={forecasts} cfg={{
          subTitle: '',
          ...question,
          forecastType: 'amount',
          defaultBounds: bounds
        }} cumulative={forecast.cumulative} chartProps={{
          watermark: 'Group',
          watermarkProps: {
            style: {
              fill: color('group'),
              fontSize: '1rem',
              opacity: 0.8
            }
          },
          primaryColor: color('group')
        }} shouldVizRelative={false} />
          </div>)}
        <div className={twMerge('grow relative', fullWidth ? 'h-[min(50vh,32rem)]' : 'h-[18rem]')}>
          {match(forecast).with({
          type: 'logistic'
        }, l => <ForecastLogisticEdit {...editCommon} onDelta={onDelta} currentForecast={l} />).with({
          type: 'drawn'
        }, d_2 => <ForecastDrawnEdit {...editCommon} onDelta={onDelta} forecast={d_2} />).exhaustive()}
        </div>
      </div>
    </WrapIf>;
};